/**
 * ProjectControlsModal - Manage control assignments for a project
 */
import React, { useState, useEffect } from 'react';
import { Modal, Switch, Tag, Spin, Button } from 'antd';
import { getProjectControls, assignProjectControls } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import { TGDataTable } from '../../../components/common';

function ProjectControlsModal({ open, onClose, project, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [controls, setControls] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);

  useEffect(() => {
    if (open && project) {
      fetchControls();
    }
  }, [open, project]);

  const fetchControls = async () => {
    setLoading(true);
    try {
      const response = await getProjectControls(project.id);
      const controlsData = response.data.controls || [];
      setControls(controlsData);

      // Set initial selected codes
      const assigned = controlsData.filter(c => c.assigned && c.enabled).map(c => c.code);
      setSelectedCodes(assigned);
    } catch (error) {
      console.error('Failed to fetch controls:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (controlCode, currentlySelected) => {
    if (currentlySelected) {
      setSelectedCodes(selectedCodes.filter(code => code !== controlCode));
    } else {
      setSelectedCodes([...selectedCodes, controlCode]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await assignProjectControls(project.id, selectedCodes);
      showSuccess(`Successfully assigned ${selectedCodes.length} controls to ${project.name}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to assign controls:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'gold';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const columns = [
    {
      key: 'enabled',
      title: 'Enabled',
      dataIndex: 'enabled',
      width: 90,
      fixed: 'left',
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={selectedCodes.includes(record.code)}
          onChange={(checked) => handleToggle(record.code, !checked)}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      key: 'code',
      title: 'Control',
      dataIndex: 'code',
      width: 90,
      fixed: 'left',
      render: (code) => <strong>{code}</strong>,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'title',
      title: 'Control Name',
      dataIndex: 'title',
      width: 250,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'domain',
      title: 'Domain',
      dataIndex: 'domain',
      width: 180,
      render: (domain) => (
        domain ? <Tag color="geekblue">{domain}</Tag> : <Tag color="default">No Domain</Tag>
      ),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'severity',
      title: 'Severity',
      dataIndex: 'severity',
      width: 110,
      render: (severity) => (
        <Tag color={getSeverityColor(severity)}>
          {severity}
        </Tag>
      ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Critical', value: 'critical' },
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' },
      ],
    },
    {
      key: 'regulation',
      title: 'Regulation',
      dataIndex: 'regulation',
      width: 410,
      render: (regulation) => (
        <Tag color="blue">{regulation}</Tag>
      ),
      sortable: true,
      filterable: true,
      filterType: 'text',
    }
  ];

  return (
    <Modal
      title={`Manage Controls - ${project?.name || ''}`}
      open={open}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
        >
          {`Save (${selectedCodes.length} selected)`}
        </Button>
      ]}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '8px', color: '#888' }}>
            {selectedCodes.length} of {controls.length} controls selected
          </div>
          <TGDataTable
            data={controls}
            columns={columns}
            loading={loading}
            rowKey="code"
            pagination="client"
            pageSize={10}
            showExport
            showColumnConfig
            showFilters
            exportFilename={`${project?.name || 'project'}_controls`}
            presetKey="trustgrid.project_controls"
          />
        </>
      )}
    </Modal>
  );
}

export default ProjectControlsModal;
